/**
 * Typed localStorage Service
 *
 * SSR-safe, typed wrappers around localStorage.
 * Pattern follows shared/utils/safeStorage.ts (sessionStorage).
 */

/**
 * All known localStorage keys used throughout the app.
 * Add new keys here to keep a single inventory.
 */
export const STORAGE_KEYS = {
  // Theme & layout
  THEME_PREFERENCE: 'theme-preference',
  SIDEBAR_COLLAPSED: 'sidebar-collapsed',

  // Auth & identity
  USER_PROFILE_SESSION: 'userProfileSession',
  USER_ID_MAPPING: 'user_id_mapping',
  AUTH_LOGOUT: 'auth_logout',
  AUTH_USER: 'auth_user',
  IS_AUTHENTICATED: 'isAuthenticated',
  STYTCH_SESSION: 'stytch_session',

  // Debug / diagnostics
  LOGGER_CONFIG: 'loggerConfig',
  SOCKET_DISCONNECT_HISTORY: '__socketDisconnectHistory',
  SOCKET_CONNECT_ERRORS: '__socketConnectErrors',
  SOCKET_DISCONNECT_CALLS: '__socketDisconnectCalls',
  TOKEN_EVENTS: '__tokenEvents',

  // Feature
  DM_COLLAPSED: 'voley.isDirectMsgsCollapsed',
  HAWKSOFT_FORM_DRAFT: 'hawksoft_form_draft',
  SYNC_JOB_ID: 'hawksoft_sync_job_id',
} as const;

/** Map storage keys → their parsed value types (read-side). */
export interface StorageValueMap {
  [STORAGE_KEYS.THEME_PREFERENCE]: 'light' | 'dark' | 'system';
  [STORAGE_KEYS.SIDEBAR_COLLAPSED]: string;
  [STORAGE_KEYS.USER_PROFILE_SESSION]: Record<string, unknown>;
  [STORAGE_KEYS.USER_ID_MAPPING]: Record<string, unknown>;
  [STORAGE_KEYS.AUTH_LOGOUT]: string;
  [STORAGE_KEYS.AUTH_USER]: string;
  [STORAGE_KEYS.IS_AUTHENTICATED]: string;
  [STORAGE_KEYS.STYTCH_SESSION]: string;
  [STORAGE_KEYS.LOGGER_CONFIG]: Record<string, unknown>;
  [STORAGE_KEYS.SOCKET_DISCONNECT_HISTORY]: unknown[];
  [STORAGE_KEYS.SOCKET_CONNECT_ERRORS]: unknown[];
  [STORAGE_KEYS.SOCKET_DISCONNECT_CALLS]: unknown[];
  [STORAGE_KEYS.TOKEN_EVENTS]: unknown[];
  [STORAGE_KEYS.DM_COLLAPSED]: string;
  [STORAGE_KEYS.HAWKSOFT_FORM_DRAFT]: Record<string, unknown>;
  [STORAGE_KEYS.SYNC_JOB_ID]: string;
}

type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

function isSSR(): boolean {
  return typeof window === 'undefined';
}

/**
 * Read and JSON-parse a value from localStorage.
 * Returns `null` when SSR, key missing, or parse fails.
 */
export function getStorageItem<K extends StorageKey>(
  key: K,
): (K extends keyof StorageValueMap ? StorageValueMap[K] : unknown) | null {
  if (isSSR()) return null;
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    return JSON.parse(raw);
  } catch {
    // Not JSON — return raw string (theme-preference, sidebar-collapsed, etc.)
    try {
      return localStorage.getItem(key) as never;
    } catch {
      return null;
    }
  }
}

/**
 * Read a raw string value from localStorage (no JSON.parse).
 * Useful for keys that store plain strings.
 */
export function getStorageRaw(key: StorageKey): string | null {
  if (isSSR()) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * JSON-stringify and write a value to localStorage.
 */
export function setStorageItem(
  key: StorageKey,
  value: unknown,
): void {
  if (isSSR()) return;
  try {
    localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  } catch {
    // Quota exceeded or storage disabled — silently fail
  }
}

/**
 * Remove a key from localStorage.
 */
export function removeStorageItem(key: StorageKey): void {
  if (isSSR()) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage disabled — silently fail
  }
}
